import React, { useState } from 'react';
import { ethers } from 'ethers';
import { Navigate } from 'react-router-dom';
import Button from '../components/common/Button';
import FullScreenLoader from '../components/loader/FullScreenLoader';
import { useWallet } from '../context/WalletContext';
import { useContractState } from '../context/ContractContext';
import { useNotify } from '../context/NotificationContext';
import { getADNFTContract } from '../web3/contractInstance';
import { parseContractError } from '../utils/errors';
import { formatUSDT, bpToPct, shortAddress } from '../utils/format';
import { toUnits } from '../web3/providerHelper';
import UserLookup from '../components/admin/UserLookup';

/** Small "current value + input + save" row used by most setters. */
function SetterRow({ label, current, onSave, placeholder, extraFields, busy }) {
  const [val, setVal] = useState('');
  const [extra, setExtra] = useState({});
  return (
    <div className="admin-row">
      <div className="label">{label}</div>
      {current !== undefined && <div className="cur">Current: <span style={{ color: 'var(--text)' }}>{current}</span></div>}
      <div className="form">
        {(extraFields || []).map((f, i) => (
          <input key={i} className="input" style={{ maxWidth: 100 }} placeholder={f.placeholder}
                 value={extra[f.name] || ''} onChange={e => setExtra({ ...extra, [f.name]: e.target.value })} />
        ))}
        <input className="input" placeholder={placeholder} value={val} onChange={e => setVal(e.target.value)} />
        <Button size="sm" onClick={() => onSave(val, extra)} loading={busy}>Save</Button>
      </div>
    </div>
  );
}

const TABS = [
  { key: 'stats',     label: 'Stats',     icon: '📊' },
  { key: 'plans',     label: 'Plans',     icon: '⚙️' },
  { key: 'users',     label: 'Users',     icon: '👥' },
  { key: 'migration', label: 'Migration', icon: '📥' }
];

export default function Admin() {
  const { getSigner } = useWallet();
  const {
    isOwner, isAdmin,
    ownerAddress, adminAddress,
    planInfo, fees, limits, contractStats, adminStats,
    tierConfig,
    refresh
  } = useContractState();
  const notify = useNotify();
  const [busyMsg, setBusyMsg] = useState('');
  const [tab, setTab] = useState('stats');

  if (!isOwner && !isAdmin) return <Navigate to="/" replace />;

  async function run(fnName, args, note) {
    const signer = getSigner();
    if (!signer) { notify.error('Wallet not ready'); return; }
    setBusyMsg(note || 'Sending transaction…');
    try {
      const c = getADNFTContract(signer);
      const tx = await c[fnName](...args);
      await tx.wait();
      notify.success('Saved', note || `${fnName} confirmed`);
      refresh();
    } catch (e) {
      notify.error('Failed', parseContractError(e));
    } finally {
      setBusyMsg('');
    }
  }

  const isAddr = (s) => ethers.utils.isAddress(s);

  return (
    <div className="page">
      <div className="admin-title">
        <h1 className="grad-text" style={{ fontSize: 22 }}>Admin Panel</h1>
        <span className="badge">{isOwner ? 'OWNER' : 'ADMIN'}</span>
      </div>
      <p className="page-subtitle">
        Owner: {ownerAddress ? shortAddress(ownerAddress) : '—'} · Admin: {adminAddress ? shortAddress(adminAddress) : '—'}
      </p>

      {/* Tab bar */}
      <div className="admin-tabs" role="tablist">
        {TABS.map(t => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            className={`admin-tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            <span className="ico">{t.icon}</span>{t.label}
          </button>
        ))}
      </div>

      {/* ---------- STATS ---------- */}
      {tab === 'stats' && (
        <>
          {adminStats && (
            <div className="admin-section">
              <h3>Platform Overview</h3>
              <div className="stat-grid">
                <div className="stat"><div className="label">Total Investment</div><div className="value">{formatUSDT(adminStats.totalInvestment)}</div></div>
                <div className="stat"><div className="label">Today Investment</div><div className="value">{formatUSDT(adminStats.todayInvestment)}</div></div>
                <div className="stat"><div className="label">Total Withdraw</div><div className="value">{formatUSDT(adminStats.totalWithdraw)}</div></div>
                <div className="stat"><div className="label">Available Withdraw</div><div className="value">{formatUSDT(adminStats.availableWithdraw)}</div></div>
              </div>
            </div>
          )}

          {contractStats && (
            <div className="admin-section">
              <h3>Contract</h3>
              <div className="stat-grid">
                <div className="stat"><div className="label">Total Users</div><div className="value">{contractStats._totalUsers ? contractStats._totalUsers.toString() : (contractStats[0] || '0').toString()}</div></div>
                <div className="stat"><div className="label">Active</div><div className="value">{(contractStats.activeCount || contractStats[2] || '0').toString()}</div></div>
                <div className="stat"><div className="label">Blacklisted</div><div className="value">{(contractStats.blacklistedCount || contractStats[3] || '0').toString()}</div></div>
                <div className="stat"><div className="label">Contract Bal</div><div className="value">{formatUSDT(contractStats.contractBalance || contractStats[4])}</div></div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ---------- PLANS ---------- */}
      {tab === 'plans' && (
        <>
          <div className="admin-section">
            <h3>Investment & Claim</h3>
            <div className="card admin-card">
              <SetterRow label="Minimum Investment (USDT)" current={limits ? formatUSDT(limits.min) : '—'} placeholder="e.g. 50"
                onSave={(v) => run('updateMinimumInvestment', [toUnits(v || '0')], 'Min investment updated')} />
              <SetterRow label="Investment Multiple (USDT)" current={limits ? formatUSDT(limits.multiple) : '—'} placeholder="e.g. 50"
                onSave={(v) => run('updateInvestmentMultiple', [toUnits(v || '0')], 'Multiple updated')} />
              <SetterRow label="Minimum Claim (USDT)" current={limits ? formatUSDT(limits.minClaim) : '—'} placeholder="e.g. 10"
                onSave={(v) => run('updateMinimumClaim', [toUnits(v || '0')], 'Min claim updated')} />
              <SetterRow label="Claim Fee (bps, 100=1%)" current={fees ? bpToPct(fees.claimFee) : '—'} placeholder="e.g. 1000"
                onSave={(v) => run('updateClaimFee', [ethers.BigNumber.from(v || '0')], 'Claim fee updated')} />
            </div>
          </div>

          <div className="admin-section">
            <h3>Trading Plan</h3>
            <div className="card admin-card">
              <SetterRow label="Trading Profit % / period (bps, 50=0.5%)" current={planInfo ? bpToPct(planInfo.dailyROI) : '—'} placeholder="e.g. 50"
                onSave={(v) => run('updateDailyROI', [ethers.BigNumber.from(v || '0')], 'Trading profit rate updated')} />
              <SetterRow label="Ceiling Multiplier (X)" current={planInfo ? `${planInfo.ceilingMultiplier}X` : '—'} placeholder="e.g. 2"
                onSave={(v) => run('updateCeilingMultiplier', [ethers.BigNumber.from(v || '0')], 'Ceiling updated')} />
              <SetterRow label="Trading Timeframe (seconds)" current={planInfo ? `${planInfo.timeframe} s` : '—'} placeholder="e.g. 86400"
                onSave={(v) => run('setROITimeframe', [ethers.BigNumber.from(v || '0')], 'Timeframe updated')} />
            </div>
          </div>

          {isOwner && (
            <div className="admin-section">
              <h3>Fund Distribution</h3>
              <div className="card admin-card">
                <SetterRow label="Team Wallet Fee (bps)" current={fees ? bpToPct(fees.teamWalletFee) : '—'} placeholder="e.g. 8000"
                  onSave={(v) => run('updateTeamWalletFee', [ethers.BigNumber.from(v || '0')], 'Team wallet fee updated')} />
                <SetterRow label="Contract Share Fee (bps)" current={fees ? bpToPct(fees.contractShareFee) : '—'} placeholder="e.g. 2000"
                  onSave={(v) => run('updateContractShareFee', [ethers.BigNumber.from(v || '0')], 'Contract share fee updated')} />
                <SetterRow label="Team Wallet Address" placeholder="0x..."
                  onSave={(v) => isAddr(v) ? run('updateTeamWallet', [v], 'Team wallet updated') : notify.error('Invalid address')} />
              </div>
            </div>
          )}

          {isOwner && (
            <div className="admin-section">
              <h3>Level Percentages (1–15)</h3>
              <div className="card admin-card">
                <SetterRow label="Set Level Percentage (bps)"
                  placeholder="new bps"
                  extraFields={[{ name: 'level', placeholder: 'lvl 1-15' }]}
                  onSave={(v, extra) => run('updateLevelPercentage',
                    [ethers.BigNumber.from((Number(extra.level) - 1) || 0), ethers.BigNumber.from(v || 0)],
                    `Level ${extra.level} % updated`)} />
              </div>
            </div>
          )}

          {isOwner && (
            <div className="admin-section">
              <h3>Unlock Tier Config</h3>
              <div className="card admin-card">
                <SetterRow
                  label="Min Investment Per Direct (USDT)"
                  current={tierConfig && tierConfig.directMinInvestment ? formatUSDT(tierConfig.directMinInvestment) : '—'}
                  placeholder="e.g. 100"
                  onSave={(v) => run('updateDirectMinInvestment', [toUnits(v || '0')], 'Direct min updated')} />
                <SetterRow
                  label="Levels Unlocked Per Qualifying Direct"
                  current={tierConfig ? String(tierConfig.levelsPerQualifyingDirect) : '—'}
                  placeholder="e.g. 3"
                  onSave={(v) => run('updateLevelsPerQualifyingDirect', [ethers.BigNumber.from(v || 0)], 'Levels-per-direct updated')} />
              </div>
            </div>
          )}
        </>
      )}

      {/* ---------- USERS ---------- */}
      {tab === 'users' && (
        <>
          <UserLookup />

          <div className="admin-section">
            <h3>Force-Unlock Levels (Per User)</h3>
            <div className="card admin-card">
              <SetterRow label="Set Unlocked Levels for User (0-15, 0 = no override)"
                placeholder="levels 0-15"
                extraFields={[{ name: 'uid', placeholder: 'user ID' }]}
                onSave={(v, extra) => {
                  const n = Number(v || 0);
                  if (n < 0 || n > 15) { notify.error('Levels must be 0-15'); return; }
                  run('setUserUnlockedLevels',
                    [ethers.BigNumber.from(extra.uid || 0), n],
                    `User ${extra.uid} unlock set to ${n}`);
                }} />
            </div>
          </div>

          <div className="admin-section">
            <h3>User Controls</h3>
            <div className="card admin-card">
              <SetterRow label="Blacklist / Unblacklist User"
                placeholder="1=blacklist, 0=unblacklist"
                extraFields={[{ name: 'uid', placeholder: 'user ID' }]}
                onSave={(v, extra) => run('blacklistUser',
                  [ethers.BigNumber.from(extra.uid || 0), v === '1' || v === 'true'],
                  `Blacklist status updated`)} />
              <SetterRow label="Virtual Register (address, referrer ID)"
                placeholder="referrer ID"
                extraFields={[{ name: 'addr', placeholder: '0x wallet' }]}
                onSave={(v, extra) => isAddr(extra.addr) ? run('virtualRegister', [extra.addr, ethers.BigNumber.from(v || 0)], 'Virtual user registered') : notify.error('Invalid address')} />
              <SetterRow label="Virtual Investment (address, amt USDT, refId)"
                placeholder="referrer ID"
                extraFields={[
                  { name: 'addr', placeholder: '0x wallet' },
                  { name: 'amt', placeholder: 'USDT' }
                ]}
                onSave={(v, extra) => isAddr(extra.addr)
                  ? run('virtualInvestment', [extra.addr, toUnits(extra.amt || '0'), ethers.BigNumber.from(v || 0)], 'Virtual investment recorded')
                  : notify.error('Invalid address')} />
            </div>
          </div>

          {isOwner && (
            <div className="admin-section">
              <h3>Ownership & Roles</h3>
              <div className="card admin-card">
                <SetterRow label="Transfer Ownership" placeholder="0x new owner"
                  onSave={(v) => isAddr(v) ? run('transferOwnership', [v], 'Ownership transferred') : notify.error('Invalid address')} />
                <SetterRow label="Set Admin" placeholder="0x new admin"
                  onSave={(v) => isAddr(v) ? run('setAdmin', [v], 'Admin updated') : notify.error('Invalid address')} />
                <SetterRow label="Set Default Referrer ID" placeholder="user ID"
                  onSave={(v) => run('setDefaultReferrer', [ethers.BigNumber.from(v || 0)], 'Default referrer updated')} />
              </div>
            </div>
          )}

          <div className="admin-section">
            <h3>Emergency</h3>
            <div className="card admin-card">
              <div className="admin-row">
                <div className="label">Emergency Withdraw (all USDT)</div>
                <div className="form">
                  <Button variant="danger" size="sm" onClick={() => {
                    if (window.confirm('Withdraw ALL USDT from the contract?')) {
                      run('emergencyWithdraw', [], 'All USDT withdrawn');
                    }
                  }}>Withdraw All</Button>
                </div>
              </div>
              <SetterRow label="Emergency Withdraw Amount (USDT)" placeholder="amount"
                onSave={(v) => run('emergencyWithdrawAmount', [toUnits(v || '0')], 'Amount withdrawn')} />
            </div>
          </div>
        </>
      )}

      {/* ---------- MIGRATION ---------- */}
      {tab === 'migration' && <AdminRegisterUserForm run={run} />}

      {busyMsg && <FullScreenLoader message={busyMsg} sub="Confirm in your wallet" />}
    </div>
  );
}

/**
 * One-shot manual migration: register a legacy user AND seed their
 * totalInvestment + directEarned + levelEarned + roiEarned in a single tx.
 * Sponsor may be given by wallet OR ID (wallet wins if both provided).
 */
function AdminRegisterUserForm({ run }) {
  const [form, setForm] = React.useState({
    userWallet: '',
    sponsorWallet: '',
    sponsorId: '',
    totalInvestment: '',
    directEarned: '',
    levelEarned: '',
    roiEarned: ''
  });
  const [busy, setBusy] = React.useState(false);

  function set(k, v) { setForm(prev => ({ ...prev, [k]: v })); }

  async function submit() {
    if (!ethers.utils.isAddress(form.userWallet)) return alert('Invalid user wallet');
    if (form.sponsorWallet && !ethers.utils.isAddress(form.sponsorWallet)) return alert('Invalid sponsor wallet');
    if (!form.totalInvestment || Number(form.totalInvestment) <= 0) return alert('Total investment must be > 0');

    const args = [
      form.userWallet,
      form.sponsorWallet || ethers.constants.AddressZero,
      ethers.BigNumber.from(form.sponsorId || '0'),
      toUnits(form.totalInvestment || '0'),
      toUnits(form.directEarned || '0'),
      toUnits(form.levelEarned || '0'),
      toUnits(form.roiEarned || '0')
    ];
    setBusy(true);
    try {
      await run('adminRegisterUser', args, 'Legacy user registered');
      setForm({ userWallet: '', sponsorWallet: '', sponsorId: '', totalInvestment: '', directEarned: '', levelEarned: '', roiEarned: '' });
    } finally { setBusy(false); }
  }

  return (
    <div className="admin-section">
      <h3>Manual Migration · Register Legacy User</h3>
      <div className="card admin-card">
        <div className="hint-msg" style={{ marginBottom: 10 }}>
          Registers the user and seeds their historical totals in one tx. Direct + Level + ROI earnings
          fill ceiling headroom immediately. No level income is paid to uplines, no team-wallet transfer.
        </div>
        <div className="field">
          <label>User Wallet *</label>
          <input className="input" placeholder="0x..." value={form.userWallet} onChange={e => set('userWallet', e.target.value)} />
        </div>
        <div className="field">
          <label>Sponsor Wallet (optional — takes precedence over Sponsor ID)</label>
          <input className="input" placeholder="0x... (leave blank to use ID / default)" value={form.sponsorWallet} onChange={e => set('sponsorWallet', e.target.value)} />
        </div>
        <div className="field">
          <label>Sponsor ID (optional, 0 = default referrer)</label>
          <input className="input" placeholder="e.g. 5000" value={form.sponsorId} onChange={e => set('sponsorId', e.target.value.replace(/\D/g, ''))} />
        </div>
        <div className="field">
          <label>Total Investment (USDT) *</label>
          <input className="input" type="number" inputMode="decimal" placeholder="e.g. 500" value={form.totalInvestment} onChange={e => set('totalInvestment', e.target.value)} />
        </div>
        <div className="field">
          <label>Direct Earned (USDT)</label>
          <input className="input" type="number" inputMode="decimal" placeholder="0" value={form.directEarned} onChange={e => set('directEarned', e.target.value)} />
        </div>
        <div className="field">
          <label>Level Earned (USDT)</label>
          <input className="input" type="number" inputMode="decimal" placeholder="0" value={form.levelEarned} onChange={e => set('levelEarned', e.target.value)} />
        </div>
        <div className="field">
          <label>ROI Earned (USDT)</label>
          <input className="input" type="number" inputMode="decimal" placeholder="0" value={form.roiEarned} onChange={e => set('roiEarned', e.target.value)} />
        </div>
        <Button neon block onClick={submit} loading={busy}>Register Legacy User</Button>
      </div>
    </div>
  );
}
